import React from "react";
import {
    Box,
    Typography,
    CardContent,
    Grid,
    Button,
    Paper,
} from "@mui/material";
import Draggable from "react-draggable";

const GDSNProductDetailsCard = ({ productData, onClose, onPrint }) => {
    if (!productData) return null;

    // Helper function to render fields
    const renderField = (label, value) => (
        <Grid item xs={12} sm={4}>
            <Typography variant="body2" sx={{ fontWeight: "bold", color: "#004085" }}>
                {label}:
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    backgroundColor: "#f5f5f5",
                    color: "#333",
                }}
            >
                {value || "N/A"}
            </Typography>
        </Grid>
    );

    return (
        <Draggable>
            <Paper
                sx={{
                    position: "absolute",
                    top: "10%",
                    left: "50%",
                    transform: "translate(-50%, -10%)",
                    zIndex: 1300,
                    width: "90%",
                    maxWidth: "1000px",
                    boxShadow: 6,
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                }}
            >
                <CardContent sx={{ padding: "20px" }}>
                    {/* Logo and Product Title */}
                    <Box sx={{ textAlign: "center", marginBottom: "20px" }}>
                        <img
                            src="https://figk-12.com/static/media/fig_CROP.cb0d7cf4314c63ba05ee.png"
                            alt="Product Placeholder"
                            style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "contain",
                                border: "1px solid #ddd",
                                borderRadius: "8px",
                            }}
                        />
                    </Box>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: "bold",
                            textAlign: "center",
                            marginBottom: "20px",
                            color: "#004085",
                        }}
                    >
                        {productData.ProductName || "Unknown Product"}
                    </Typography>

                    {/* General Information Section */}
                    <Box sx={{ marginBottom: "20px" }}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: "#ffffff",
                                fontWeight: "bold",
                                backgroundColor: "#007bff",
                                padding: "10px",
                                borderRadius: "5px",
                                textAlign: "center",
                            }}
                        >
                            General Information
                        </Typography>
                        <Grid container spacing={2} sx={{ marginTop: "10px" }}>
                            {renderField("GTIN", productData.GTIN)}
                            {renderField("UPC", productData.UPC)}
                            {renderField("Brand Name", productData.BrandName)}
                            {renderField("Manufacturer", productData.ManufacturerText1Ws)}
                            {renderField("Description", productData.Description)}
                            {renderField("Manufacturer Code", productData.ManufacturerProductCode)}
                            {renderField("Category ID", productData.CategoryId)}
                            {renderField("Subcategory ID", productData.SubCategoryId)}
                            {renderField("IOC Category ID", productData.IOCCategoryId)}
                            {renderField("Is Active", productData.IsActive ? "Yes" : "No")}
                            {renderField("Vendor", productData.Vendor)}
                            {renderField("Allergen Keywords", productData.AllergenKeywords)}
                        </Grid>
                    </Box>

                    {/* Nutritional Information Section */}
                    <Box sx={{ marginBottom: "20px" }}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: "#ffffff",
                                fontWeight: "bold",
                                backgroundColor: "#007bff",
                                padding: "10px",
                                borderRadius: "5px",
                                textAlign: "center",
                            }}
                        >
                            Nutritional Information
                        </Typography>
                        <Grid container spacing={2} sx={{ marginTop: "10px" }}>
                            {renderField("Serving", `${productData.Serving || "N/A"} ${productData.ServingUom || ""}`)}
                            {renderField("Calories", `${productData.Calories || "N/A"} ${productData.CaloriesUom || ""}`)}
                            {renderField("Carbohydrates", `${productData.Carbohydrates || "N/A"} ${productData.CarbohydratesUom || ""}`)}
                            {renderField("Protein", `${productData.Protein || "N/A"} ${productData.ProteinUom || ""}`)}
                            {renderField("Total Fat", productData.TotalFat)}
                            {renderField("Trans Fat", productData.TransFat)}
                            {renderField("Saturated Fat", productData.SaturatedFat)}
                            {renderField("Dietary Fiber", `${productData.DietaryFiber || "N/A"} ${productData.DietaryFiberUom || ""}`)}
                            {renderField("Sugar", `${productData.Sugar || "N/A"} ${productData.SugarUom || ""}`)}
                            {renderField("Added Sugar", `${productData.AddedSugar || "N/A"} ${productData.AddedSugarUom || ""}`)}
                            {renderField("Sodium", `${productData.Sodium || "N/A"} ${productData.SodiumUom || ""}`)}
                            {renderField("Cholesterol", `${productData.Cholesterol || "N/A"} ${productData.CholesterolUOM || ""}`)}
                        </Grid>
                    </Box>

                    {/* Additional Fields Section */}
                    <Box sx={{ marginBottom: "20px" }}>
                        <Typography
                            variant="h6"
                            sx={{
                                color: "#ffffff",
                                fontWeight: "bold",
                                backgroundColor: "#007bff",
                                padding: "10px",
                                borderRadius: "5px",
                                textAlign: "center",
                            }}
                        >
                            Additional Information
                        </Typography>
                        <Grid container spacing={2} sx={{ marginTop: "10px" }}>
                            {renderField("Old Product ID", productData.OldProductId)}
                            {renderField("Storage Type ID", productData.StorageTypeId)}
                            {renderField("Is Merged", productData.IsMerge ? "Yes" : "No")}
                            {renderField("Merge Date", productData.MergeDate || "N/A")}
                            {renderField("Allergen Flag", productData.AllergenFlag)}
                            {renderField("Is One World Sync Product", productData.IsOneWorldSyncProduct ? "Yes" : "No")}
                            {renderField("Product Label PDF URL", productData.ProductLablePdfUrl)}
                        </Grid>
                    </Box>

                    {/* Ingredients Section */}
                    {productData.Ingredients && (
                        <Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: "#ffffff",
                                    fontWeight: "bold",
                                    backgroundColor: "#007bff",
                                    padding: "10px",
                                    borderRadius: "5px",
                                    textAlign: "center",
                                }}
                            >
                                Ingredients
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    padding: "12px",
                                    border: "1px solid #ddd",
                                    borderRadius: "5px",
                                    backgroundColor: "#f5f5f5",
                                    whiteSpace: "pre-wrap",
                                    marginTop: "10px",
                                    color: "#333",
                                }}
                            >
                                {productData.Ingredients}
                            </Typography>
                        </Box>
                    )}

                    {/* Action Buttons */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "20px",
                        }}
                    >
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: "#007bff",
                                color: "#fff",
                                marginRight: "10px",
                                "&:hover": { backgroundColor: "#0056b3" },
                            }}
                            onClick={onPrint}
                        >
                            Print
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={onClose}
                        >
                            Close
                        </Button>
                    </Box>
                </CardContent>
            </Paper>
        </Draggable>
    );
};

export default GDSNProductDetailsCard;
